import type { ScenarioSchedule } from '@seaber/common/core/scenario';
import type { ClientId } from '@seaber/common/lib/clientId';
import type {
  OpenPositionsFragment,
  VesselReservationFragment,
  CreateVesselReservationMutation,
  CreateVesselReservationMutationVariables,
  DeleteVesselReservationMutation,
  DeleteVesselReservationMutationVariables,
  CreateOpenPositionMutation,
  CreateOpenPositionMutationVariables,
  DeleteOpenPositionMutation,
  DeleteOpenPositionMutationVariables,
} from '../../../graphql';
import {
  CreateVesselReservationDocument,
  DeleteVesselReservationDocument,
  CreateOpenPositionDocument,
  DeleteOpenPositionDocument,
} from '../../../graphql';
import type { useApolloClient } from '@apollo/client';

/**
 * Syncs vessel reservations and open positions from an estimate timeline to the database.
 * - Creates items that exist on estimate but not on main
 * - Deletes items that exist on main but not on estimate
 */
const applyEstimateReservationsAndOpenPositions = async ({
  estimateId,
  scenarios,
  currentVesselReservations,
  currentOpenPositions,
  client,
}: {
  estimateId: ClientId;
  scenarios: ScenarioSchedule[];
  currentVesselReservations: VesselReservationFragment[];
  currentOpenPositions: OpenPositionsFragment[];
  client: ReturnType<typeof useApolloClient>;
}) => {
  const scenarioWithEstimate = scenarios.find((s) =>
    s.estimateTimelines?.some((e) => e.clientId === estimateId),
  );
  const estimate = scenarioWithEstimate?.estimateTimelines?.find(
    (e) => e.clientId === estimateId,
  );

  if (!estimate) {
    return;
  }

  const vesselId = estimate.vesselId;
  const estimateReservations = estimate.vesselReservations ?? [];
  const estimateOpenPositions = estimate.openPositions ?? [];

  const mainReservationsForVessel = currentVesselReservations.filter(
    (r) => r.vesselId === vesselId,
  );
  const mainOpenPositionsForVessel = currentOpenPositions.filter(
    (op) => op.vesselId === vesselId,
  );

  const mainReservationIds = new Set(
    mainReservationsForVessel.map((r) => r.id),
  );
  const mainOpenPositionIds = new Set(
    mainOpenPositionsForVessel.map((op) => op.id),
  );
  const estimateReservationIds = new Set(estimateReservations.map((r) => r.id));
  const estimateOpenPositionIds = new Set(
    estimateOpenPositions.map((op) => op.id),
  );

  const mutations: Array<Promise<unknown>> = [];

  // Create reservations that exist on estimate but not on main
  for (const vesselReservation of estimateReservations) {
    if (!mainReservationIds.has(vesselReservation.id)) {
      mutations.push(
        client.mutate<
          CreateVesselReservationMutation,
          CreateVesselReservationMutationVariables
        >({
          mutation: CreateVesselReservationDocument,
          variables: {
            input: {
              vesselReservation: {
                duration: vesselReservation.duration,
                endPortId: vesselReservation.endPortId,
                endRegionId: vesselReservation.endRegionId,
                expireTime: vesselReservation.expireTime,
                remark: vesselReservation.remark,
                startPortId: vesselReservation.startPortId,
                startRegionId: vesselReservation.startRegionId,
                startTime: vesselReservation.startTime,
                type: vesselReservation.type,
                vesselId: vesselReservation.vesselId,
              },
            },
          },
        }),
      );
    }
  }

  // Delete reservations that exist on main but not on estimate
  for (const reservation of mainReservationsForVessel) {
    if (!estimateReservationIds.has(reservation.id)) {
      mutations.push(
        client.mutate<
          DeleteVesselReservationMutation,
          DeleteVesselReservationMutationVariables
        >({
          mutation: DeleteVesselReservationDocument,
          variables: { id: reservation.id },
        }),
      );
    }
  }

  // Create open positions that exist on estimate but not on main
  for (const openPosition of estimateOpenPositions) {
    if (!mainOpenPositionIds.has(openPosition.id)) {
      mutations.push(
        client.mutate<
          CreateOpenPositionMutation,
          CreateOpenPositionMutationVariables
        >({
          mutation: CreateOpenPositionDocument,
          variables: {
            input: {
              openPosition: {
                directionId: openPosition.directionId,
                remark: openPosition.remark,
                startPortId: openPosition.startPortId,
                startTime: openPosition.startTime,
                vesselId: openPosition.vesselId,
              },
            },
          },
        }),
      );
    }
  }

  // Delete open positions that exist on main but not on estimate
  for (const openPosition of mainOpenPositionsForVessel) {
    if (!estimateOpenPositionIds.has(openPosition.id)) {
      mutations.push(
        client.mutate<
          DeleteOpenPositionMutation,
          DeleteOpenPositionMutationVariables
        >({
          mutation: DeleteOpenPositionDocument,
          variables: {
            openPositionId: openPosition.id,
          },
        }),
      );
    }
  }

  if (mutations.length > 0) {
    await Promise.all(mutations);
  }
};

export default applyEstimateReservationsAndOpenPositions;
